import { PropTypes } from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import CreateTemplateModal from "./components/CreateTemplateModal";
import TemplateSelect from "./components/TemplateSelect";
import { getCurrentUserId, isAdminUser } from "./helpers/currentUser";
import useTemplateManager from "./hooks/useTemplateManager";

const TaskTemplateSelect = ({
  applyTemplate = () => false,
  name = "",
  category = "",
  description = "",
  priority = "",
  owner_id = null,
  status = "",
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = isAdminUser();
  const currentUserId = getCurrentUserId();

  const formState = useMemo(
    () => ({ name, category, description, priority, owner_id, status }),
    [name, category, description, priority, owner_id, status],
  );

  const isDifferent = useCallback((currentFormState, template) => {
    const nameChanged = currentFormState.name !== (template.name || "");
    const categoryChanged =
      currentFormState.category !== (template.category || "follow_up");
    const descriptionChanged =
      currentFormState.description !== (template.description || "");
    const priorityChanged =
      currentFormState.priority !== (template.priority || "normal");
    const ownerChanged =
      (currentFormState.owner_id ?? null) !== (template.owner_id ?? null);
    const statusChanged = currentFormState.status !== (template.status || "");
    return (
      nameChanged ||
      categoryChanged ||
      descriptionChanged ||
      priorityChanged ||
      ownerChanged ||
      statusChanged
    );
  }, []);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    variablesWarning,
    setVariablesWarning,
    variablesWarningRetry,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate: trackApply,
  } = useTemplateManager({
    apiPath: "/task_templates",
    apiResourceName: "task_template",
    templateType: "task",
    displayNameSingular: "Task Template",
    displayNamePlural: "Task Templates",
    formState,
    isDifferent,
    handleUnsupportedVariables: true,
    onTemplateCreated: () => {
      setShowCreateModal(false);
    },
  });

  const handleCreate = (payload) => {
    createTemplate(() => payload);
  };

  const updateTaskTemplate = () => {
    if (selectedTemplate?.value) {
      updateTemplate(() => ({
        id: selectedTemplate.value,
        name,
        category,
        description,
        priority,
        owner_id,
        status,
      }));
    }
  };

  return (
    <>
      <TemplateSelect
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        templateChanged={templateChanged}
        setTemplateChanged={setTemplateChanged}
        isLoading={isLoading}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        isCopying={isCopying}
        isSharing={isSharing}
        updateTemplate={updateTaskTemplate}
        deleteTemplate={deleteTemplate}
        copyTemplate={copyTemplate}
        shareTemplate={shareTemplate}
        unshareTemplate={unshareTemplate}
        trackApply={trackApply}
        applyTemplate={applyTemplate}
        onCreate={() => setShowCreateModal(true)}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        infoTooltip="Templates save your task name, description, category, priority, owner, and status. Due date and related contacts are set per task."
      />
      <CreateTemplateModal
        templateType="task"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
        isAdmin={isAdmin}
        formValues={formState}
      />
      <UnsupportedVariablesWarning
        show={!!variablesWarning}
        warning={variablesWarning}
        saving={isCreating || isUpdating}
        onReview={() => setVariablesWarning(null)}
        onSaveAnyway={() => {
          setVariablesWarning(null);
          variablesWarningRetry?.();
        }}
      />
    </>
  );
};

TaskTemplateSelect.propTypes = {
  applyTemplate: PropTypes.func,
  name: PropTypes.string,
  category: PropTypes.string,
  description: PropTypes.string,
  priority: PropTypes.string,
  owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  status: PropTypes.string,
};

export default TaskTemplateSelect;
